import { nativeCamera, type NativeCameraSession, type NativeFrame } from '@/lib/nativeCamera'
import { WebSegmenter, NativeMask, type Head } from '@/components/camera/segmenter'
import { ChromaKeyer, KEY_DEFAULT, chromaOf, rgbHex, type KeyOpts } from '@/components/camera/chromaKey'
import type { Fit, StoredItem } from '@/components/camera/recordStore'

/**
 * Record's composite (Brian's record.html v3.2: the Oct 22 commits and Oct 29 to
 * Nov 3): the canvas that is both the preview and exactly what is recorded,
 * 720 x 1280 upright or 1280 x 720 wide (the computer), at 30 fps.
 *
 *   Split screen: every split comes from one layout table (panesFor), shared
 *   with the computer's panel and the phone's wheel: you alone, side by side
 *   (and flipped), stacked (the photo above you, 40/60), you in a window over
 *   the photo, or the photo in a window over you, the window at any corner,
 *   Small / Medium / Large. Before anything is added the chosen layout shows
 *   its empty pane (dashed); a take that starts with nothing added has you
 *   fill the frame (Oct 22: no blank space in a recording).
 *   Green screen: the picture full frame behind you, cut out (MediaPipe in a
 *   browser, the selfie multiclass model on a computer; the phone's own in the
 *   app), at Full, Left, Right or in a Circle that follows your head. With a
 *   real green or blue cloth behind you, Green cloth keys it out instead
 *   (chromaKey.ts), in a browser and in the app alike.
 *
 * The teleprompter is never drawn here, so it is never recorded.
 */

export type RecMode = 'split' | 'green'

/** Where you stand in green screen: s your size (1 = full frame), x your centre, y your bottom edge. */
export interface MePos { s: number; x: number; y: number; c?: 1; f?: string; r?: string }
export const ME: Record<'full' | 'left' | 'right' | 'circle', MePos> = {
  full: { s: 1, x: 0.5, y: 1 },
  left: { s: 0.58, x: 0.3, y: 1 },
  right: { s: 0.58, x: 0.7, y: 1 },
  circle: { s: 0.4, x: 0.26, y: 0.96, c: 1 },
}

/** The layouts (Oct 29, Brian's log #015: "window placement options with icons, like on a Blackmagic ATEM"). */
export type LayKind = 'full' | 'side' | 'sidef' | 'stack' | 'youwin' | 'mediawin'
export type Corner = 'tl' | 'tr' | 'bl' | 'br'
export type WinSize = 's' | 'm' | 'l'
export interface Lay { kind: LayKind; corner: Corner; size: WinSize }
export const LAY_KINDS: LayKind[] = ['full', 'side', 'sidef', 'stack', 'youwin', 'mediawin']
export const CORNERS: Corner[] = ['tl', 'tr', 'bl', 'br']
/** A window's share of the frame: Small, Medium, Large. */
export const WIN: Record<WinSize, number> = { s: 0.26, m: 0.34, l: 0.44 }
export interface PaneRect { x: number; y: number; w: number; h: number }
/** Where you and the photo or clip sit: media is null when you are alone; win says which one is the window; seam where they meet. */
export interface Panes { you: PaneRect; media: PaneRect | null; win?: 'you' | 'media'; seam?: 'h' | 'v' | 'vf' }

export function winRect(corner: Corner, size: WinSize, w: number, h: number): PaneRect {
  const f = WIN[size] || WIN.m, ww = w * f, wh = h * f, m = Math.min(w, h) * 0.035
  return { x: corner.endsWith('l') ? m : w - ww - m, y: corner.startsWith('t') ? m : h - wh - m, w: ww, h: wh }
}

/** The panes of a layout in a w x h frame. */
export function panesFor(kind: LayKind, w: number, h: number, corner: Corner = 'br', size: WinSize = 'm'): Panes {
  const R = (x: number, y: number, pw: number, ph: number): PaneRect => ({ x, y, w: pw, h: ph })
  if (kind === 'side') return { you: R(0, 0, w / 2, h), media: R(w / 2, 0, w / 2, h), seam: 'v' }
  if (kind === 'sidef') return { media: R(0, 0, w / 2, h), you: R(w / 2, 0, w / 2, h), seam: 'vf' }
  if (kind === 'stack') { const gh = Math.round(h * SPLIT); return { media: R(0, 0, w, gh), you: R(0, gh, w, h - gh), seam: 'h' } }
  if (kind === 'youwin') return { media: R(0, 0, w, h), you: winRect(corner, size, w, h), win: 'you' }
  if (kind === 'mediawin') return { you: R(0, 0, w, h), media: winRect(corner, size, w, h), win: 'media' }
  return { you: R(0, 0, w, h), media: null }
}

/**
 * The layout a frame actually uses. Side by side doesn't fit a 9:16 frame, so
 * it stacks; on the phone a stacked pick in a wide frame sits side by side.
 */
export function layKindFor(kind: LayKind, vertical: boolean, desk: boolean): LayKind {
  if (vertical) return kind === 'side' || kind === 'sidef' ? 'stack' : kind
  return !desk && kind === 'stack' ? 'side' : kind
}

export interface Item {
  id: number
  type: 'img' | 'video'
  el: HTMLImageElement | HTMLVideoElement
  blob: Blob
  url: string
  dur: number
  start: number
  end: number
  label: string
  fit: Fit | null
  playing: boolean
  thumb: string
  node?: MediaElementAudioSourceNode | null
  cov?: HTMLCanvasElement
  covKey?: string
  /** A stand-in (?demo=1): never saved to the gallery. */
  demo?: boolean
  stopDemo?: () => void
}

export const MAX_ITEMS = 5
export const MAX_CLIP = 120
const SPLIT = 0.4

let nextId = 1

export function itemFromBlob(blob: Blob, type: 'img' | 'video', label?: string): Promise<Item | null> {
  return new Promise((res) => {
    const url = URL.createObjectURL(blob)
    const base = { id: nextId++, blob, url, start: 0, fit: null, playing: false, thumb: '' }
    if (type === 'video' || /^video\//.test(blob.type)) {
      const v = document.createElement('video')
      v.muted = true; v.playsInline = true; v.preload = 'auto'; v.crossOrigin = 'anonymous'; v.src = url
      v.onloadeddata = () => {
        const it: Item = { ...base, type: 'video', el: v, dur: v.duration || 0, end: Math.min(v.duration || 0, MAX_CLIP), label: label || 'Clip' }
        it.thumb = thumbOf(it)
        res(it)
      }
      v.onerror = () => res(null)
    } else {
      const im = new Image()
      im.onload = () => { const it: Item = { ...base, type: 'img', el: im, dur: 0, end: 0, label: label || 'Photo' }; it.thumb = thumbOf(it); res(it) }
      im.onerror = () => res(null)
      im.src = url
    }
  })
}

export async function itemFromStored(s: StoredItem): Promise<Item | null> {
  const it = await itemFromBlob(s.blob, s.type, s.label)
  if (!it) return null
  it.start = s.start || 0
  it.end = s.end || it.end
  it.fit = s.fit
  return it
}

/**
 * The stand-ins for boards and checks (?demo=1). Oct 29 (Brian: "get rid of the
 * sample images and replace with solid colors, instead of a sunset"): the photos
 * are plain colour fields; the clip is one too, with only its running time in a
 * corner so you can see it play.
 */
const PAL = ['#3b5b6e', '#5c7a5a', '#6b4a5e', '#8a6a3e']
export async function demoItems(): Promise<Item[]> {
  const out: Item[] = []
  for (const i of [0, 1]) {
    const c = document.createElement('canvas'); c.width = 960; c.height = 720
    const g = c.getContext('2d')!; g.fillStyle = PAL[i % PAL.length]; g.fillRect(0, 0, 960, 720)
    const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/png'))
    const it = blob ? await itemFromBlob(blob, 'img', `Photo ${i + 1}`) : null
    if (it) { it.demo = true; out.push(it) }
  }
  const clip = demoClip()
  if (clip) out.push(clip)
  return out
}
function demoClip(): Item | null {
  const c = document.createElement('canvas'); c.width = 960; c.height = 720
  if (!c.captureStream) return null
  const g = c.getContext('2d')!, v = document.createElement('video')
  let t = 0, last = performance.now(), raf = 0
  const draw = () => {
    const now = performance.now(); if (!v.paused) t = Math.min(12, t + (now - last) / 1000); last = now
    g.fillStyle = '#2f4858'; g.fillRect(0, 0, 960, 720)
    g.fillStyle = 'rgba(255,255,255,.55)'; g.font = '600 26px Figtree, sans-serif'; g.textAlign = 'right'
    g.fillText(`0:${String(Math.min(12, Math.floor(t))).padStart(2, '0')} / 0:12`, 920, 680); g.textAlign = 'left'
    raf = requestAnimationFrame(draw)
  }
  draw()
  v.muted = true; v.playsInline = true; v.srcObject = c.captureStream(30)
  // a stream has no clock of its own: the running time restarts when the clip is primed
  Object.defineProperty(v, 'currentTime', { get: () => t, set: (x: number) => { t = Math.max(0, +x || 0) }, configurable: true })
  Object.defineProperty(v, 'videoWidth', { get: () => 960, configurable: true })
  Object.defineProperty(v, 'videoHeight', { get: () => 720, configurable: true })
  v.pause()
  const thumb = (() => { try { const k = document.createElement('canvas'); k.width = 160; k.height = 160; const kg = k.getContext('2d')!; kg.fillStyle = '#2f4858'; kg.fillRect(0, 0, 160, 160); return k.toDataURL('image/jpeg', 0.7) } catch { return '' } })()
  return { id: nextId++, type: 'video', el: v, blob: new Blob(), url: '', dur: 12, start: 0, end: 12, label: 'Demo clip', fit: null, playing: false, thumb, demo: true,
    stopDemo: () => { cancelAnimationFrame(raf); try { (v.srcObject as MediaStream | null)?.getTracks().forEach((tr) => tr.stop()) } catch { /* */ } } }
}

export const toStored = (it: Item): StoredItem => ({ type: it.type, blob: it.blob, start: it.start, end: it.end, label: it.label, fit: it.fit })

export function thumbOf(it: Item): string {
  try {
    const c = document.createElement('canvas'); c.width = 160; c.height = 160
    const g = c.getContext('2d')!
    const z = srcSize(it), m = Math.min(z.w, z.h)
    if (!m) return ''
    g.drawImage(it.el, (z.w - m) / 2, (z.h - m) / 2, m, m, 0, 0, 160, 160)
    return c.toDataURL('image/jpeg', 0.7)
  } catch { return '' }
}

export const clipLen = (it: Item) => (it.type === 'video' ? Math.max(0, (it.end || it.dur) - (it.start || 0)) : 0)
const srcSize = (it: Item) => it.type === 'video'
  ? { w: (it.el as HTMLVideoElement).videoWidth, h: (it.el as HTMLVideoElement).videoHeight }
  : { w: (it.el as HTMLImageElement).naturalWidth, h: (it.el as HTMLImageElement).naturalHeight }
const fitOf = (it: Item): Fit => ({ s: 1, x: 0.5, y: 0.5, ...(it.fit || {}) })
function cropOf(f: Fit, sw: number, sh: number, w: number, h: number) {
  const tar = w / h
  let cw = sw, ch = sh
  if (sw / sh > tar) cw = sh * tar; else ch = sw / tar
  cw /= f.s; ch /= f.s
  const cx = Math.max(cw / 2, Math.min(sw - cw / 2, f.x * sw)), cy = Math.max(ch / 2, Math.min(sh - ch / 2, f.y * sh))
  return { cx: cx - cw / 2, cy: cy - ch / 2, cw, ch }
}

/** Picks what the recorder can write: mp4 first (iPhone, and what Photos wants), then webm. */
export function pickMime(): string {
  const c = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return ''
  return c.find((x) => { try { return MediaRecorder.isTypeSupported(x) } catch { return false } }) || ''
}

export interface EngineEvents {
  /** Every drawn frame (about 30 a second): the teleprompter and the timer ride on it. */
  onTick?: (now: number) => void
  /** The camera came on (or went to the stand-in). */
  onCamera?: (live: boolean, native: boolean) => void
  /** A clip started or stopped playing. */
  onClip?: (playing: boolean, ended: boolean) => void
  /** Green screen loading state. */
  onSeg?: (state: 'loading' | 'ready' | 'failed') => void
}

export class RecordEngine {
  canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  W = 720
  H = 1280
  ev: EngineEvents

  mode: RecMode = 'split'
  mirror = true
  beauty = false
  me: MePos = { ...ME.full }
  brand = { f: '#c8854a', r: '#ffffff' }
  items: Item[] = []
  cur = 0
  /** The chosen layout; the computer picks from the whole table, the phone from its four. */
  lay: Lay = { kind: 'stack', corner: 'br', size: 'm' }
  desk = false
  /** A countdown or a take is on: with nothing added, you fill the frame. */
  taking = false
  /** Green cloth: key out a real backdrop instead of the person cutout. */
  cloth = false
  key: KeyOpts = { ...KEY_DEFAULT }
  /** Picking the key colour: green screen shows the plain camera until a click on the backdrop. */
  picking = false

  // the camera
  private video: HTMLVideoElement
  private stream: MediaStream | null = null
  camLive = false
  native: NativeCameraSession | null = null
  private nframe: { bmp: ImageBitmap | HTMLImageElement; w: number; h: number } | null = null
  private nmask = new NativeMask()
  private seg: WebSegmenter | null = null
  private face: Head | null = null
  private keyer: ChromaKeyer | null = null

  // drawing scratch
  private tmpC: HTMLCanvasElement | null = null
  private camC: HTMLCanvasElement | null = null
  private raf = 0
  private last = 0
  private alive = true

  // sound and the recorder
  mic: MediaStream | null = null
  private actx: AudioContext | null = null
  private mixDest: MediaStreamAudioDestinationNode | null = null
  private clipGain: GainNode | null = null
  private micNode: MediaStreamAudioSourceNode | null = null
  clipMuted = false
  private recorder: MediaRecorder | null = null
  private chunks: Blob[] = []

  constructor(canvas: HTMLCanvasElement, ev: EngineEvents) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d', { alpha: false })!
    this.ev = ev
    this.video = document.createElement('video')
    this.video.muted = true; this.video.playsInline = true; this.video.autoplay = true
    this.raf = requestAnimationFrame(this.render)
  }

  // ---------- frame shape ----------
  setVertical(vert: boolean) {
    const cw = vert ? 720 : 1280, ch = vert ? 1280 : 720
    if (this.canvas.width !== cw || this.canvas.height !== ch) { this.canvas.width = cw; this.canvas.height = ch }
    this.W = cw; this.H = ch
  }
  get vertical() { return this.H > this.W }

  // ---------- the camera ----------
  get usingNative() { return !!this.native }

  async startCamera(): Promise<boolean> {
    if (nativeCamera.available() && !this.native) {
      this.native = nativeCamera.session(this.onNativeFrame, (why) => {
        console.warn('native camera: no frames (' + why + '), using the browser camera')
        this.native = null; this.nframe = null; this.nmask.clear(); this.camLive = false
        void this.startCamera()
      })
      await this.native.start({ mask: this.wantsMask(), beauty: this.beauty ? 0.5 : 0 })
      return true
    }
    if (this.native) return true
    if (!navigator.mediaDevices?.getUserMedia) { this.setLive(false); return false }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
      if (!this.alive) { this.stream.getTracks().forEach((t) => t.stop()); return false }
      this.video.srcObject = this.stream
      await this.video.play().catch(() => undefined)
      this.setLive(true)
      if (this.wantsMask()) void this.loadSeg()
      return true
    } catch {
      this.setLive(false)
      return false
    }
  }

  private setLive(live: boolean) {
    if (this.camLive === live && live) return
    this.camLive = live
    this.ev.onCamera?.(live, !!this.native)
  }

  private onNativeFrame = (f: NativeFrame) => {
    if (f.mask && f.mask !== 'keep') this.nmask.set(f.mask.data, f.mask.width, f.mask.height)
    else if (!f.mask) this.nmask.clear()
    const old = this.nframe?.bmp
    if (old && 'close' in old) old.close()
    this.nframe = { bmp: f.bitmap, w: f.width, h: f.height }
    if (!this.camLive) this.setLive(true)
  }

  private camEl(): CanvasImageSource | null { return this.native ? this.nframe?.bmp ?? null : this.video }
  private camW() { return this.native ? this.nframe?.w ?? 0 : this.video.videoWidth }
  private camH() { return this.native ? this.nframe?.h ?? 0 : this.video.videoHeight }
  private camReady() { return this.camLive && (this.native ? !!this.nframe : this.video.readyState >= 2) }

  /** The person cutout is wanted: green screen without Green cloth. */
  private wantsMask() { return this.mode === 'green' && !this.cloth }

  /** Green screen in the browser, loaded on first use: the multiclass model on a computer, the small one elsewhere. */
  async loadSeg(): Promise<boolean> {
    if (this.native) return true
    if (!this.seg) this.seg = new WebSegmenter()
    if (this.seg.ready) return true
    this.ev.onSeg?.('loading')
    const ok = await this.seg.load(this.desk)
    this.ev.onSeg?.(ok ? 'ready' : 'failed')
    return ok
  }
  /** Which model runs and how fast (for checks). */
  segStats() { return this.seg?.stats ?? null }

  setMode(m: RecMode) {
    this.mode = m
    if (m !== 'green') this.picking = false
    this.syncMask()
  }

  /** Green cloth on or off: the app stops (or starts) Vision's mask; a browser loads the model only when it is needed. */
  setCloth(on: boolean) {
    this.cloth = on
    if (!on) this.picking = false
    this.syncMask()
  }

  private syncMask() {
    if (this.native) this.native.setMask(this.wantsMask())
    else if (this.wantsMask() && this.camLive) void this.loadSeg()
  }

  /** The camera picture, small, for sampling colours. */
  private sampleCam(): { d: Uint8ClampedArray; w: number; h: number } | null {
    const cam = this.camEl(), vw = this.camW(), vh = this.camH()
    if (!cam || !this.camReady() || !vw || !vh) return null
    const w = 160, h = Math.max(2, Math.round((160 * vh) / vw))
    const c = document.createElement('canvas'); c.width = w; c.height = h
    const g = c.getContext('2d', { willReadFrequently: true })!
    g.drawImage(cam, 0, 0, w, h)
    try { return { d: g.getImageData(0, 0, w, h).data, w, h } } catch { return null }
  }
  private avg(px: { d: Uint8ClampedArray; w: number; h: number }, x0: number, y0: number, x1: number, y1: number) {
    let r = 0, g = 0, b = 0, n = 0
    for (let y = Math.max(0, y0 | 0); y < Math.min(px.h, y1); y++) for (let x = Math.max(0, x0 | 0); x < Math.min(px.w, x1); x++) {
      const j = (y * px.w + x) * 4; r += px.d[j]; g += px.d[j + 1]; b += px.d[j + 2]; n++
    }
    return n ? rgbHex(r / n, g / n, b / n) : null
  }

  /**
   * The eyedropper: the key colour under a point on the canvas (canvas pixels),
   * read from the camera as the picker shows it (full frame, mirrored). Null with no camera.
   */
  pickAt(px: number, py: number): string | null {
    const s = this.sampleCam(); if (!s) return null
    const { W, H } = this, ar = s.w / s.h, tar = W / H
    let cx = 0, cy = 0, cw = s.w, ch = s.h
    if (ar > tar) { cw = s.h * tar; cx = (s.w - cw) / 2 } else { ch = s.w / tar; cy = (s.h - ch) / 2 }
    let fx = px / W; if (this.mirror) fx = 1 - fx
    const x = cx + fx * cw, y = cy + (py / H) * ch
    const hex = this.avg(s, x - 2, y - 2, x + 3, y + 3)
    if (hex) { this.key = { ...this.key, color: hex }; this.picking = false }
    return hex
  }

  /**
   * Auto: the backdrop's colour from the top corners and the edges of the camera
   * picture, where a cloth shows round you. Null when that is not a strong colour.
   */
  autoKey(): string | null {
    const s = this.sampleCam(); if (!s) return null
    const { w, h } = s, e = Math.round(w * 0.12)
    const picks = [this.avg(s, 0, 0, e, h * 0.3), this.avg(s, w - e, 0, w, h * 0.3), this.avg(s, w * 0.3, 0, w * 0.7, h * 0.08), this.avg(s, 0, h * 0.3, e * 0.6, h * 0.6), this.avg(s, w - e * 0.6, h * 0.3, w, h * 0.6)]
      .filter((x): x is string => !!x).sort((a, b) => chromaOf(b) - chromaOf(a))
    // the strongest two agree on a cloth; one strong corner alone may be a poster
    const best = picks[0], second = picks[1]
    if (!best || chromaOf(best) < 0.35 || !second || chromaOf(second) < 0.25) return null
    return best
  }

  setBeauty(on: boolean) { this.beauty = on; this.native?.setBeauty(on ? 0.5 : 0) }
  orient() { this.native?.orient() }

  // ---------- drawing ----------
  private render = (now: number) => {
    if (!this.alive) return
    this.raf = requestAnimationFrame(this.render)
    if (now - this.last < 28) return // 30 fps is plenty, and half the work
    this.last = now
    try { this.draw(now / 1000) } catch (e) { console.warn('draw', e) }
    this.clipTick()
    this.ev.onTick?.(now)
  }

  /** What the frame shows now (the layout table, the frame's shape, whether anything is added, whether a take is on). */
  panes(): Panes {
    const { W, H } = this
    if (!this.items.length && this.taking) return panesFor('full', W, H)
    return panesFor(layKindFor(this.lay.kind, this.vertical, this.desk), W, H, this.lay.corner, this.lay.size)
  }

  private draw(t: number) {
    const { ctx, W, H } = this
    const it = this.items[this.cur]
    if (this.mode === 'green') {
      if (this.picking) { this.drawCam(0, 0, W, H, t); return }
      this.drawItem(it, 0, 0, W, H)
      const b = this.meBox()
      if (this.me.c) this.drawCircle(b, t)
      else {
        ctx.save(); ctx.translate(b.dx, b.dy); ctx.scale(this.me.s, this.me.s)
        if (!this.drawCutout(0, 0, W, H)) this.drawStandIn(0, 0, W, H, t, true)
        ctx.restore()
      }
      return
    }
    // every split comes from the layout table; before anything is added the chosen layout shows its empty pane
    const P = this.panes()
    const you = (r: PaneRect) => this.drawCam(r.x, r.y, r.w, r.h, t)
    const media = (r: PaneRect) => (it ? this.drawItem(it, r.x, r.y, r.w, r.h) : this.drawEmptyPane(r))
    if (!P.media) { you(P.you); return }
    if (P.win === 'you') { media(P.media); this.drawWindow(P.you, () => you(P.you)); return }
    if (P.win === 'media') { you(P.you); const M = P.media; this.drawWindow(M, () => media(M)); return }
    const M = P.media
    media(M); you(P.you)
    // a shadow along the seam
    let fade: CanvasGradient
    if (P.seam === 'h') {
      fade = ctx.createLinearGradient(0, M.y + M.h - H * 0.06, 0, M.y + M.h); fade.addColorStop(0, 'rgba(0,0,0,0)'); fade.addColorStop(1, 'rgba(0,0,0,.6)')
      ctx.fillStyle = fade; ctx.fillRect(0, M.y + M.h - H * 0.06, W, H * 0.06)
    } else if (P.seam === 'v') {
      fade = ctx.createLinearGradient(M.x, 0, M.x + W * 0.12, 0); fade.addColorStop(0, 'rgba(0,0,0,.8)'); fade.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = fade; ctx.fillRect(M.x, 0, W * 0.12, H)
    } else {
      fade = ctx.createLinearGradient(M.x + M.w, 0, M.x + M.w - W * 0.12, 0); fade.addColorStop(0, 'rgba(0,0,0,.8)'); fade.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = fade; ctx.fillRect(M.x + M.w - W * 0.12, 0, W * 0.12, H)
    }
  }

  private rr(r: PaneRect, rad: number) {
    const ctx = this.ctx
    ctx.beginPath()
    if (ctx.roundRect) ctx.roundRect(r.x, r.y, r.w, r.h, rad); else ctx.rect(r.x, r.y, r.w, r.h)
  }

  /** A picture-in-picture window: rounded, a thin light edge and a shadow. */
  private drawWindow(r: PaneRect, fn: () => void) {
    const ctx = this.ctx, rad = Math.min(r.w, r.h) * 0.06
    ctx.save(); ctx.shadowColor = 'rgba(0,0,0,.55)'; ctx.shadowBlur = Math.min(r.w, r.h) * 0.12; ctx.shadowOffsetY = 6; ctx.fillStyle = '#000'; this.rr(r, rad); ctx.fill(); ctx.restore()
    ctx.save(); this.rr(r, rad); ctx.clip(); fn(); ctx.restore()
    ctx.save(); this.rr(r, rad); ctx.lineWidth = Math.max(2, Math.min(r.w, r.h) * 0.012); ctx.strokeStyle = 'rgba(255,255,255,.6)'; ctx.stroke(); ctx.restore()
  }

  /** The empty pane before anything is added: a dashed frame where the photo will go. */
  private drawEmptyPane(r: PaneRect) {
    const ctx = this.ctx, m = Math.min(r.w, r.h) * 0.06
    ctx.fillStyle = '#232327'; ctx.fillRect(r.x, r.y, r.w, r.h)
    ctx.save(); ctx.setLineDash([12, 9]); ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,255,255,.22)'
    this.rr({ x: r.x + m, y: r.y + m, w: r.w - 2 * m, h: r.h - 2 * m }, m * 0.5); ctx.stroke(); ctx.restore()
  }

  private drawCam(x: number, y: number, w: number, h: number, t: number) {
    const src = this.camEl()
    if (src && this.camReady()) this.drawCover(src, this.camW(), this.camH(), x, y, w, h, this.mirror)
    else this.drawStandIn(x, y, w, h, t)
  }

  private drawStandIn(x: number, y: number, w: number, h: number, t: number, cut = false) {
    const ctx = this.ctx
    if (!cut) { const gr = ctx.createLinearGradient(0, y, 0, y + h); gr.addColorStop(0, '#1e1e21'); gr.addColorStop(1, '#141210'); ctx.fillStyle = gr; ctx.fillRect(x, y, w, h) }
    const cx = x + w / 2, bob = Math.sin(t * 1.6) * 4, s = Math.min(w, h * 1.1) / 720
    ctx.fillStyle = cut ? '#5a524a' : '#4a443d'
    ctx.beginPath(); ctx.ellipse(cx, y + h - 20 * s + bob * 0.3, 260 * s, 190 * s, 0, Math.PI, 0); ctx.fill()
    ctx.beginPath(); ctx.arc(cx, y + h - 290 * s + bob, 105 * s, 0, 7); ctx.fill()
    if (cut) { ctx.strokeStyle = 'rgba(255,255,255,.2)'; ctx.lineWidth = 3; ctx.stroke() }
  }

  private drawCover(src: CanvasImageSource, sw: number, sh: number, x: number, y: number, w: number, h: number, mir: boolean) {
    if (!sw || !sh) return
    const ctx = this.ctx, ar = sw / sh, tar = w / h
    let cx = 0, cy = 0, cw = sw, ch = sh
    if (ar > tar) { cw = sh * tar; cx = (sw - cw) / 2 } else { ch = sw / tar; cy = (sh - ch) / 2 }
    ctx.save()
    if (mir) { ctx.translate(x + w, y); ctx.scale(-1, 1); ctx.drawImage(src, cx, cy, cw, ch, 0, 0, w, h) } else ctx.drawImage(src, cx, cy, cw, ch, x, y, w, h)
    ctx.restore()
    this.soften(ctx, src, cx, cy, cw, ch, x, y, w, h, mir)
  }

  /** The browser's Smooth: the camera again, blurred and faint, over itself. The app does the real thing. */
  private soften(g: CanvasRenderingContext2D, src: CanvasImageSource, cx: number, cy: number, cw: number, ch: number, x: number, y: number, w: number, h: number, mir: boolean) {
    if (!this.beauty || this.native) return
    g.save(); g.beginPath(); g.rect(x, y, w, h); g.clip()
    try { g.filter = 'blur(5px)' } catch { /* */ }
    g.globalAlpha = 0.4
    if (mir) { g.translate(x + w, y); g.scale(-1, 1); g.drawImage(src, cx, cy, cw, ch, 0, 0, w, h) } else g.drawImage(src, cx, cy, cw, ch, x, y, w, h)
    g.restore()
  }

  private drawItem(it: Item | undefined, x: number, y: number, w: number, h: number) {
    const ctx = this.ctx
    if (!it) { ctx.fillStyle = '#232327'; ctx.fillRect(x, y, w, h); return }
    const z = srcSize(it)
    if (!z.w) { ctx.fillStyle = '#232327'; ctx.fillRect(x, y, w, h); return }
    const f = fitOf(it)
    if (it.type === 'video') { const c = cropOf(f, z.w, z.h, w, h); ctx.drawImage(it.el, c.cx, c.cy, c.cw, c.ch, x, y, w, h); return }
    // A phone photo is big: scale it to the frame once and draw that copy each frame.
    const k = `${w}x${h}|${f.s.toFixed(3)},${f.x.toFixed(3)},${f.y.toFixed(3)}`
    if (!it.cov || it.covKey !== k) {
      const c = document.createElement('canvas'); c.width = w; c.height = h
      const cr = cropOf(f, z.w, z.h, w, h)
      c.getContext('2d')!.drawImage(it.el, cr.cx, cr.cy, cr.cw, cr.ch, 0, 0, w, h)
      it.cov = c; it.covKey = k
    }
    ctx.drawImage(it.cov, x, y)
  }

  meBox() {
    const { W, H, me } = this
    if (me.c) { const d = Math.min(W, H) * me.s; return { dw: d, dh: d, dx: me.x * W - d / 2, dy: me.y * H - d } }
    const dw = W * me.s, dh = H * me.s
    return { dw, dh, dx: me.x * W - dw / 2, dy: me.y * H - dh }
  }

  private fillOf() { return this.me.f === 'brand' ? this.brand.f : this.me.f || '' }
  private ringOf() { return this.me.r === 'match' ? this.fillOf() : this.me.r === 'brand' ? this.brand.r : 'rgba(255,255,255,.9)' }

  /** The camera picture in a round frame; the crop follows the head the mask finds, eased so it glides. */
  private drawCircle(b: ReturnType<RecordEngine['meBox']>, t: number) {
    const ctx = this.ctx, d = b.dw, cx = b.dx + d / 2, cy = b.dy + d / 2, r = d / 2
    ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip()
    const src = this.camEl()
    if (src && this.camReady()) {
      // run the mask so the head can be found, even with the plain picture
      if (!this.native && !this.cloth && this.seg?.ready) this.seg.update(this.video, this.video.videoWidth, this.video.videoHeight)
      const faceT = this.native ? this.nmask.head : this.seg?.head ?? null
      if (faceT) {
        if (!this.face) this.face = { ...faceT }
        else { this.face.x += (faceT.x - this.face.x) * 0.15; this.face.y += (faceT.y - this.face.y) * 0.15; this.face.w += (faceT.w - this.face.w) * 0.1 }
      }
      const vw = this.camW(), vh = this.camH(), f = this.face || { x: 0.5, y: 0.34, w: 0.3 }
      const side = Math.max(Math.min(vw, vh) * 0.4, Math.min(Math.min(vw, vh), f.w * vw * 3))
      // the mask is unmirrored; the picture is drawn mirrored, so the crop's x is taken on the raw frame
      const sx = Math.max(0, Math.min(vw - side, f.x * vw - side / 2)), sy = Math.max(0, Math.min(vh - side, f.y * vh - side / 2))
      let drawn = false
      const fill = this.fillOf()
      if (fill) { ctx.fillStyle = fill; ctx.fillRect(b.dx, b.dy, d, d); drawn = this.drawCutout(b.dx, b.dy, d, d, { sx, sy, sw: side, sh: side }) }
      if (!drawn) {
        ctx.save()
        if (this.mirror) { ctx.translate(b.dx + d, b.dy); ctx.scale(-1, 1); ctx.drawImage(src, sx, sy, side, side, 0, 0, d, d) } else ctx.drawImage(src, sx, sy, side, side, b.dx, b.dy, d, d)
        ctx.restore()
      }
    } else this.drawStandIn(b.dx, b.dy, d, d, t)
    ctx.restore()
    const ring = this.ringOf()
    if (ring) { ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r - d * 0.012, 0, Math.PI * 2); ctx.lineWidth = Math.max(3, d * 0.024); ctx.strokeStyle = ring; ctx.stroke(); ctx.restore() }
  }

  /** You, cut out of the camera frame, drawn at x,y,w,h. False when there is no mask yet. */
  private drawCutout(x: number, y: number, w: number, h: number, src?: { sx: number; sy: number; sw: number; sh: number }): boolean {
    let mask: HTMLCanvasElement | null
    const cam = this.camEl()
    if (!cam || !this.camReady()) return false
    if (this.cloth) mask = null
    else if (this.native) mask = this.nmask.canvas
    else mask = this.seg?.ready ? this.seg.update(this.video, this.video.videoWidth, this.video.videoHeight) : null
    if (!mask && !this.cloth) return false
    w = Math.round(w); h = Math.round(h)
    if (!this.tmpC || this.tmpC.width !== w || this.tmpC.height !== h) {
      this.tmpC = document.createElement('canvas'); this.tmpC.width = w; this.tmpC.height = h
      this.camC = document.createElement('canvas'); this.camC.width = w; this.camC.height = h
    }
    const tc = this.tmpC.getContext('2d')!, cc = this.camC!.getContext('2d')!
    const vw = this.camW(), vh = this.camH(), ar = vw / vh, tar = w / h
    let sx = 0, sy = 0, sw = vw, sh = vh
    if (ar > tar) { sw = vh * tar; sx = (vw - sw) / 2 } else { sh = vw / tar; sy = (vh - sh) / 2 }
    if (src) { sx = src.sx; sy = src.sy; sw = src.sw; sh = src.sh }
    // The camera goes onto a plain canvas first: iOS WebKit ignores the blend mode with a live video as the source.
    cc.globalCompositeOperation = 'source-over'; cc.save()
    if (this.mirror) { cc.translate(w, 0); cc.scale(-1, 1) }
    cc.drawImage(cam, sx, sy, sw, sh, 0, 0, w, h); cc.restore()
    this.soften(cc, cam, sx, sy, sw, sh, 0, 0, w, h, this.mirror)
    if (!mask) {
      // Green cloth: the key replaces the mask (it is per pixel at the size drawn, so no blur)
      if (!this.keyer) this.keyer = new ChromaKeyer()
      this.ctx.drawImage(this.keyer.key(this.camC!, this.key), x, y, w, h)
      return true
    }
    tc.globalCompositeOperation = 'source-over'; tc.clearRect(0, 0, w, h); tc.drawImage(this.camC!, 0, 0)
    const mw = mask.width, mh = mask.height
    tc.globalCompositeOperation = 'destination-in'; tc.save()
    try { tc.filter = this.native ? 'blur(1px)' : 'blur(2px)' } catch { /* */ }
    if (this.mirror) { tc.translate(w, 0); tc.scale(-1, 1) }
    tc.drawImage(mask, (sx / vw) * mw, (sy / vh) * mh, (sw / vw) * mw, (sh / vh) * mh, 0, 0, w, h)
    tc.restore(); tc.globalCompositeOperation = 'source-over'
    this.ctx.drawImage(this.tmpC, x, y)
    return true
  }

  // ---------- the pictures behind you ----------
  /** Pan and zoom the picture: a finger's move by a fraction of the frame moves it the same fraction of what shows. */
  panFit(it: Item, f0: Fit, s: number, fx: number, fy: number) {
    const z = srcSize(it); if (!z.w) return
    const f = { ...f0, s: Math.max(1, Math.min(4, s)) }
    const c = cropOf(f, z.w, z.h, this.W, this.H)
    f.x = Math.max(0, Math.min(1, f0.x - (fx * c.cw) / z.w))
    f.y = Math.max(0, Math.min(1, f0.y - (fy * c.ch) / z.h))
    it.fit = f
  }
  fitOf(it: Item) { return fitOf(it) }

  go(i: number) {
    if (!this.items.length) return
    this.stopClips()
    this.cur = ((i % this.items.length) + this.items.length) % this.items.length
    this.primeClip(this.items[this.cur])
  }

  // ---------- clips: paused and silent until played ----------
  get clipPlaying() { const it = this.items[this.cur]; return !!it?.playing }

  playClip(it = this.items[this.cur]) {
    if (!it || it.type !== 'video') return
    this.wireClip(it); this.setClipMute(false)
    const v = it.el as HTMLVideoElement, end = (it.end || it.dur) - 0.05
    if (v.ended || v.currentTime >= end || v.currentTime < (it.start || 0)) { try { v.currentTime = it.start || 0 } catch { /* */ } }
    void v.play().catch(() => undefined)
    it.playing = true
    this.ev.onClip?.(true, false)
  }

  pauseClip(it = this.items[this.cur]) {
    if (!it?.playing) return
    it.playing = false
    ;(it.el as HTMLVideoElement).pause()
    this.ev.onClip?.(false, false)
  }

  primeClip(it?: Item) {
    if (!it || it.type !== 'video') return
    if (it.playing) this.pauseClip(it)
    try { (it.el as HTMLVideoElement).currentTime = it.start || 0 } catch { /* */ }
  }

  stopClips() {
    let any = false
    for (const i of this.items) if (i.playing) { i.playing = false; (i.el as HTMLVideoElement).pause(); any = true }
    if (any) this.ev.onClip?.(false, false)
  }

  private clipTick() {
    const it = this.items[this.cur]
    if (!it?.playing || it.type !== 'video') return
    const v = it.el as HTMLVideoElement
    if (v.currentTime >= (it.end || it.dur) - 0.05 || v.ended) { it.playing = false; v.pause(); this.ev.onClip?.(false, true) }
  }

  // ---------- sound: the microphone and the clips, mixed ----------
  /** Made (or resumed) inside the record tap: iOS suspends a context made outside one. */
  audioGraph(): AudioContext | null {
    if (this.actx) { if (this.actx.state === 'suspended') void this.actx.resume().catch(() => undefined) }
    else {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!AC) return null
      try {
        this.actx = new AC()
        this.mixDest = this.actx.createMediaStreamDestination()
        this.clipGain = this.actx.createGain(); this.clipGain.gain.value = 1
        this.clipGain.connect(this.mixDest); this.clipGain.connect(this.actx.destination)
      } catch { this.actx = null; return null }
    }
    if (this.mic && !this.micNode) { try { this.micNode = this.actx.createMediaStreamSource(this.mic); this.micNode.connect(this.mixDest!) } catch { this.micNode = null } }
    return this.actx
  }

  private wireClip(it: Item) {
    if (!this.audioGraph() || it.node) return
    try {
      it.node = this.actx!.createMediaElementSource(it.el as HTMLVideoElement)
      it.node.connect(this.clipGain!)
      ;(it.el as HTMLVideoElement).muted = false; (it.el as HTMLVideoElement).volume = 1
    } catch { it.node = null }
  }

  setClipMute(m: boolean) {
    this.clipMuted = m
    if (this.clipGain && this.actx) this.clipGain.gain.setTargetAtTime(m ? 0 : 1, this.actx.currentTime, 0.02)
    else for (const i of this.items) if (i.type === 'video' && !i.node) (i.el as HTMLVideoElement).muted = m
  }

  /** The microphone, asked for at the moment of recording, without phone-call processing. */
  async askMic() {
    if (this.mic || !navigator.mediaDevices?.getUserMedia) return
    const FULL: MediaStreamConstraints = { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: { ideal: 2 }, sampleRate: { ideal: 48000 } } }
    try { this.mic = await navigator.mediaDevices.getUserMedia(FULL) } catch {
      try { this.mic = await navigator.mediaDevices.getUserMedia({ audio: true }) } catch { this.mic = null }
    }
    if (this.mic && this.actx && !this.micNode) this.audioGraph()
  }

  // ---------- the recorder ----------
  get recording() { return !!this.recorder && this.recorder.state !== 'inactive' }

  startRecording(): boolean {
    this.chunks = []
    this.recorder = null
    if (typeof MediaRecorder === 'undefined' || !this.canvas.captureStream) return false
    try {
      const st = this.canvas.captureStream(30)
      // the mix only while its context runs; otherwise the microphone's own track
      const mixOk = !!(this.audioGraph() && this.mixDest && this.actx?.state === 'running' && this.micNode)
      if (mixOk) this.mixDest!.stream.getAudioTracks().forEach((tr) => st.addTrack(tr))
      else this.mic?.getAudioTracks().forEach((tr) => st.addTrack(tr))
      const type = pickMime()
      this.recorder = new MediaRecorder(st, type
        ? { mimeType: type, videoBitsPerSecond: 4_000_000, audioBitsPerSecond: 256_000 }
        : { audioBitsPerSecond: 256_000 })
      this.recorder.ondataavailable = (e) => { if (e.data?.size) this.chunks.push(e.data) }
      this.recorder.start(500)
      return true
    } catch { this.recorder = null; return false }
  }

  /** Stops the take. The blob is null when this browser cannot record the canvas. */
  stopRecording(): Promise<Blob | null> {
    const r = this.recorder
    this.recorder = null
    if (!r || r.state === 'inactive') return Promise.resolve(null)
    return new Promise((res) => {
      r.onstop = () => res(this.chunks.length ? new Blob(this.chunks, { type: r.mimeType || 'video/webm' }) : null)
      try { r.stop() } catch { res(null) }
    })
  }

  poster(): string { try { return this.canvas.toDataURL('image/jpeg', 0.7) } catch { return '' } }

  // ---------- leaving ----------
  destroy() {
    this.alive = false
    cancelAnimationFrame(this.raf)
    try { if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop() } catch { /* */ }
    this.native?.stop(); this.native = null
    this.stream?.getTracks().forEach((t) => t.stop())
    this.mic?.getTracks().forEach((t) => t.stop())
    this.stopClips()
    try { void this.actx?.close() } catch { /* */ }
    this.seg?.close()
    this.keyer?.close()
    for (const i of this.items) { if (i.url) URL.revokeObjectURL(i.url); i.stopDemo?.() }
  }
}
